import moment from "moment";
import { useMemo } from "react";
import {
  resolveMilestones,
  resolvePhaseColor,
} from "../../utils/annual-planner-helpers";

const findPhaseForWeek = (datesAttachments, phasesList, selectedWeekUtc) => {
  const attachment = (datesAttachments || []).find(
    (a) => Number(a.dateAttachment) === selectedWeekUtc
  );
  if (!attachment) return null;
  return {
    name: attachment.phaseName,
    color: resolvePhaseColor(attachment.phaseName, phasesList),
  };
};

// Feeds the Weekly Notes panel: the selected week's training + nutrition
// phase, every upcoming milestone (no cap, per the user) with a countdown
// relative to that week, and the week's own note (if any). Past milestones
// are dropped here - a countdown doesn't make sense for something already
// behind the selected week.
export const useWeekNotesData = ({
  selectedWeek,
  datesAttachments,
  phases,
  nutritionDatesAttachments,
  nutritionPhases,
  clientGoals,
  events,
  phasesDescriptions,
}) => {
  // Depends only on clientGoals/events, not selectedWeek - without its own
  // memo, clicking the stepper (which only changes selectedWeek) re-sorted
  // and rebuilt this from scratch on every click, even though the merge
  // itself never changed.
  const allMilestones = useMemo(
    () => resolveMilestones(clientGoals, events),
    [clientGoals, events]
  );

  return useMemo(() => {
    const selectedWeekUtc = Number(moment(selectedWeek).format("YYYYMMDD"));

    const trainingPhase = findPhaseForWeek(
      datesAttachments,
      phases,
      selectedWeekUtc
    );
    const nutritionPhase = findPhaseForWeek(
      nutritionDatesAttachments,
      nutritionPhases,
      selectedWeekUtc
    );

    const milestones = allMilestones
      .filter(
        (milestone) =>
          !milestone.completed && milestone.utcDate >= selectedWeekUtc
      )
      .map((milestone) => ({
        ...milestone,
        countdownWeeks: moment(String(milestone.utcDate), "YYYYMMDD").diff(
          selectedWeek,
          "weeks"
        ),
      }));

    const weekComment =
      (phasesDescriptions || []).find(
        (description) => Number(description.dateAttachment) === selectedWeekUtc
      ) ?? null;

    return { trainingPhase, nutritionPhase, milestones, weekComment };
  }, [
    selectedWeek,
    datesAttachments,
    phases,
    nutritionDatesAttachments,
    nutritionPhases,
    allMilestones,
    phasesDescriptions,
  ]);
};
